import { getShipperScopeUids } from "../shipper/applyShipperScopeParams";

export const VEHICLE_LIST_LIMIT = 100;
export const VEHICLE_SEARCH_MIN_LENGTH = 3;

/** Filtros de busca: apenas Placa, Tipo e RENAVAM. */
export const EMPTY_VEHICLE_FILTERS = {
  vehiclePlate: "",
  vehicleType: "",
  renavam: "",
};

export const VEHICLE_FILTER_FIELDS = Object.keys(EMPTY_VEHICLE_FILTERS);

/**
 * Normaliza termo de busca: vazio limpa o filtro; abaixo do mínimo não dispara busca.
 * @returns {string|null} string pronta para filtro, ou null quando ainda não deve buscar.
 */
export function normalizeVehicleSearchTerm(
  value,
  minLength = VEHICLE_SEARCH_MIN_LENGTH,
) {
  const term = String(value ?? "").trim();
  if (!term) return "";
  if (term.length < minLength) return null;
  return term;
}

/**
 * Normaliza placa para busca (remove hífen/espaços/pontos; upper-case).
 * Vazio → ""; abaixo do mínimo → null (não dispara busca).
 */
export function normalizeVehiclePlateTerm(
  value,
  minLength = VEHICLE_SEARCH_MIN_LENGTH,
) {
  const normalized = String(value ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase();
  if (!normalized) return "";
  if (normalized.length < minLength) return null;
  return normalized;
}

/** Termo com letras e dígitos (ex.: BIA-2342 / BIA2342) → busca por placa. */
export function isLikelyVehiclePlateTerm(value) {
  const normalized = String(value ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toUpperCase();
  return /[A-Z]/.test(normalized) && /\d/.test(normalized);
}

/** Termo só com dígitos → busca por RENAVAM. */
export function isLikelyRenavamTerm(value) {
  const cleaned = String(value ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .trim();
  return cleaned.length >= VEHICLE_SEARCH_MIN_LENGTH && /^\d+$/.test(cleaned);
}

/**
 * Monta query params de listagem (escopo + filtros ativos).
 * Retorna null quando o usuário não tem escopo válido (lista vazia).
 */
export function buildVehicleListParams(user, filters = EMPTY_VEHICLE_FILTERS) {
  const params = {
    page: 1,
    limit: VEHICLE_LIST_LIMIT,
    trailersFilter: "tractors",
  };

  const allowedUids = getShipperScopeUids(user);

  if (allowedUids === null) {
    params.viewAll = "true";
  } else if (allowedUids.length === 0) {
    return null;
  } else {
    applyShipperParams(params, user, allowedUids);
  }

  applyActiveFilters(params, filters);
  return params;
}

function applyShipperParams(params, user, allowedUids) {
  if (user.uidShipper) {
    params.uidShipper = user.uidShipper;
  }
  if (
    Array.isArray(user.uidShipperAffiliates) &&
    user.uidShipperAffiliates.length
  ) {
    params.affiliates = user.uidShipperAffiliates.join(",");
  }
  if (!params.uidShipper && allowedUids.length > 0) {
    params.uidShipper = allowedUids[0];
    if (allowedUids.length > 1) {
      params.affiliates = allowedUids.slice(1).join(",");
    }
  }
}

function applyActiveFilters(params, filters) {
  for (const field of VEHICLE_FILTER_FIELDS) {
    const value =
      field === "vehiclePlate"
        ? normalizeVehiclePlateTerm(filters[field])
        : normalizeVehicleSearchTerm(filters[field]);
    if (value) {
      params[field] = value;
    }
  }
}

export function unwrapVehicleListItems(response) {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

export function unwrapVehicleListTotal(response, fallbackItems = []) {
  const payload = response?.data;
  const count = payload?.count;
  if (typeof count === "number" && Number.isFinite(count)) return count;
  return fallbackItems.length;
}

export function toCreatedAtSeconds(value) {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime() / 1000;
  if (typeof value?.seconds === "number") return value.seconds;
  if (typeof value?._seconds === "number") return value._seconds;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? value / 1000 : value;
  }
  if (typeof value === "string") {
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? 0 : ms / 1000;
  }
  return 0;
}

export function sortVehiclesByCreatedAtDesc(items) {
  return [...items].sort(
    (a, b) => toCreatedAtSeconds(b?.createdAt) - toCreatedAtSeconds(a?.createdAt),
  );
}

/** Valor controlado do input da lupa (evita reset ao apagar). */
export function resolveFilterInputValue(selectedKeys, activeFilter) {
  if (!Array.isArray(selectedKeys)) {
    return activeFilter ?? "";
  }
  // length > 0 inclui [""] quando o usuário apagou o texto.
  if (selectedKeys.length > 0) {
    return selectedKeys[0] ?? "";
  }
  // [] = dropdown acabou de abrir / ainda sem edição → mostra filtro ativo.
  return activeFilter ?? "";
}

/** Keys enviadas ao buscar (dropdown ou filtro já aplicado). */
export function resolveFilterSearchKeys(selectedKeys, activeFilter) {
  if (Array.isArray(selectedKeys) && selectedKeys.length > 0) {
    return selectedKeys;
  }
  return activeFilter ? [activeFilter] : [""];
}

export function canViewVehicle(user) {
  return Boolean(
    user?.permissoes?.vehicle?.isView || user?.permissoes?.veiculo?.visualizar,
  );
}

export function canEditVehicle(user) {
  return Boolean(
    user?.permissoes?.vehicle?.isEdit || user?.permissoes?.veiculo?.editar,
  );
}

export function canDeleteVehicle(user) {
  return Boolean(
    user?.permissoes?.vehicle?.isDelete || user?.permissoes?.veiculo?.excluir,
  );
}

export function canCreateVehicle(user) {
  return Boolean(
    user?.permissoes?.vehicle?.isCreate || user?.permissoes?.veiculo?.cadastrar,
  );
}

export function compareText(a, b) {
  return String(a || "").localeCompare(String(b || ""));
}

/** Match local (fallback) para colunas de busca da listagem. */
export function recordMatchesVehicleFilter(record, dataIndex, rawTerm) {
  const term = String(rawTerm ?? "").trim();
  if (!term) return true;

  if (dataIndex === "vehiclePlate") {
    const plate = String(record?.vehiclePlate ?? "")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase();
    const normalizedTerm = term.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (normalizedTerm && plate.includes(normalizedTerm)) return true;
    return String(record?.vehiclePlate ?? "")
      .toLowerCase()
      .includes(term.toLowerCase());
  }

  if (dataIndex === "renavam") {
    const renavam = String(record?.renavam ?? "").replace(/\D/g, "");
    const normalizedTerm = term.replace(/\D/g, "");
    return Boolean(normalizedTerm && renavam.includes(normalizedTerm));
  }

  if (dataIndex === "vehicleType") {
    const type = String(record?.vehicleType ?? "");
    if (type.toLowerCase().includes(term.toLowerCase())) return true;
    const normalizedType = type.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const normalizedTerm = term.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    return Boolean(
      normalizedTerm && normalizedType.includes(normalizedTerm),
    );
  }

  return String(record?.[dataIndex] ?? "")
    .toLowerCase()
    .includes(term.toLowerCase());
}

/**
 * Classificação da linha: Reboque somente quando trailers/trailer === true.
 * Qualquer outro valor (false, null, undefined ou campo ausente) vira Veículo.
 */
export function getVehicleClassification(row) {
  return row?.trailers === true || row?.trailer === true
    ? "Reboque"
    : "Veículo";
}
